import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { buildAppointmentWhatsappLink } from "@/lib/whatsappLinks";
import RescheduleRequestsInbox from "@/components/doctor/RescheduleRequestsInbox";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import TimeSlotSelect from "@/components/common/TimeSlotSelect";
import {
    Calendar, Phone, CheckCircle, XCircle, WhatsappLogo, Plus, Lightning,
    User, Clock, ArrowsClockwise, UserPlus, MapPin, Warning, ListChecks,
    ArrowUUpLeft, ListBullets, CalendarBlank, Receipt, ChatCircleDots,
    EnvelopeSimple, Stethoscope, Buildings, IdentificationCard, X,
    MagnifyingGlass, ArrowRight,
} from "@phosphor-icons/react";

/* ─── Appointment type config — drives the dropdown + default durations,
   plus the human-readable label. Order matters: the most common types
   are first. */
const APPT_TYPES = [
    { value: "consultation",     label: "Consultation",        duration: 30 },
    { value: "scan",             label: "Scan / impression",   duration: 30 },
    { value: "delivery",         label: "Aligner delivery",    duration: 15 },
    { value: "adjustment",       label: "Adjustment / review", duration: 20 },
    { value: "refinement_scan",  label: "Refinement scan",     duration: 30 },
    { value: "retainer_check",   label: "Retainer check",      duration: 20 },
    { value: "emergency",        label: "Emergency / repair",  duration: 20 },
    { value: "video_consult",    label: "Video consultation",  duration: 15 },
];
const TYPE_LABEL = Object.fromEntries(APPT_TYPES.map((t) => [t.value, t.label]));
const LEGACY_LABEL = { first_fitting: "First fitting", review: "Review", refinement: "Refinement", general: "General visit" };
const typeLabel = (v) => TYPE_LABEL[v] || LEGACY_LABEL[v] || (v || "Appointment");

/* iter98.99n — Clinic working-hours grid in 15-minute increments. The
 * BookingDialog renders this as a clean shadcn <Select> instead of the
 * native `<input type="time">` spinner. Range: 07:00 → 21:45 (covers
 * early-morning consults through late-evening reviews). */
const APPT_TIME_SLOTS = (() => {
    const out = [];
    for (let h = 7; h <= 21; h += 1) {
        for (let m = 0; m < 60; m += 15) {
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
})();

/* Format "13:30" → "1:30 PM" for human display. */
const fmtSlotHuman = (hhmm) => {
    if (!hhmm) return "";
    const [hStr, mStr] = hhmm.split(":");
    let h = parseInt(hStr, 10);
    const m = mStr;
    const period = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m} ${period}`;
};

/* ─── Status badge config — clinical colour vocabulary the receptionist
   recognizes at a glance. `arrived` is the urgent action signal. */
const STATUS_STYLE = {
    scheduled:    { label: "Scheduled",      cls: "bg-slate-100 text-slate-700" },
    pending:      { label: "Scheduled",      cls: "bg-slate-100 text-slate-700" },
    confirmed:    { label: "Confirmed",      cls: "bg-emerald-100 text-emerald-800" },
    arrived:      { label: "Arrived",        cls: "bg-amber-100 text-amber-800" },
    in_progress:  { label: "In session",     cls: "bg-blue-100 text-blue-800" },
    completed:    { label: "Completed",      cls: "bg-blue-50 text-blue-600" },
    cancelled:    { label: "Cancelled",      cls: "bg-rose-100 text-rose-700" },
    no_show:      { label: "No-show",        cls: "bg-rose-50 text-rose-600 line-through" },
    rescheduled:  { label: "Rescheduled",    cls: "bg-purple-50 text-purple-600 line-through" },
};

const ACTIVE_STATES = new Set(["scheduled", "pending", "confirmed", "arrived", "in_progress"]);

const todayISO = () => {
    // Use LOCAL date components — not `toISOString().slice(0,10)`. In any
    // timezone with a positive UTC offset (e.g. IST UTC+5:30) the ISO
    // version returns yesterday's date during the early-morning local
    // hours, which is wrong for "today's calendar".
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/* iter98.99m — Helpers used by BookingDialog to snap times to a
 * 15-minute grid and block bookings in the past. The `<input type="time">`
 * `step="900"` attribute snaps user-typed values to :00 / :15 / :30 / :45
 * and the `min` attribute (set only when date === today) prevents past
 * slots in the picker. The submit handler revalidates on the server-side
 * round-trip so direct typing is also caught. */
const nowHHMM = () => {
    const d = new Date();
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
};

const roundUpToQuarter = (hhmm) => {
    if (!hhmm || !hhmm.includes(":")) return "09:00";
    const [hStr, mStr] = hhmm.split(":");
    let h = parseInt(hStr, 10);
    let m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return "09:00";
    // Round minutes up to next multiple of 15.
    const rounded = Math.ceil(m / 15) * 15;
    if (rounded >= 60) {
        h = (h + 1) % 24;
        m = 0;
    } else {
        m = rounded;
    }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const nextBookableTime = (dateStr) => {
    // For today, snap to the next quarter-hour from now. For future dates
    // default to a sane working-hour start.
    if (dateStr === todayISO()) {
        const snapped = roundUpToQuarter(nowHHMM());
        // Don't allow past 21:45 — push to next-day-style fallback. We
        // still return a 23:45 max so the picker stays valid; the caller
        // is responsible for nudging the doctor to pick tomorrow.
        return snapped;
    }
    return "10:00";
};

function humanPatientId(patientId) {
    if (!patientId) return "—";
    return `INV-PT-${patientId.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
}

export default function DoctorAppointments() {
    const { user } = useAuth();
    const [appts, setAppts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("upcoming");
    const [query, setQuery] = useState("");
    const [openBook, setOpenBook] = useState(false);
    const [openWalkIn, setOpenWalkIn] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [rescheduleTarget, setRescheduleTarget] = useState(null);
    const [view, setView] = useState("list");          // "list" | "day" | "week" | "month"
    const [dayDate, setDayDate] = useState(todayISO()); // anchor date for day/week/month
    // Drag-to-reschedule state. When the user drops a block on a new slot
    // we open the reschedule dialog pre-filled — never a silent move.
    const [pendingDrag, setPendingDrag] = useState(null); // {appt, date, time}
    // Preview modal — opens when the user clicks a row body. Centered
    // Dialog (matches the My Patients pattern, not a side drawer).
    const [previewAppt, setPreviewAppt] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/appointments/doctor");
            setAppts(r.data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    /* ─── Search filter (applied to every view) ──────────────────────
       Search lives at page-level so it works in List, Day, Week and
       Month views alike — the receptionist always has one click to find
       a specific patient regardless of which lens is active. */
    const queryFiltered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return appts;
        return appts.filter((a) =>
            (a.patient_name || "").toLowerCase().includes(q) ||
            (a.patient_phone || "").toLowerCase().includes(q) ||
            humanPatientId(a.patient_id).toLowerCase().includes(q) ||
            typeLabel(a.appointment_type || a.type).toLowerCase().includes(q),
        );
    }, [appts, query]);

    /* ─── Bucket the search-filtered set into the 3 list sub-tabs.
       (We dropped the "Today" sub-tab — it's now covered by the
       always-visible Today panel + the Day view set to today, so
       keeping it as a list filter was creating four ways to see the
       same data.) */
    const buckets = useMemo(() => {
        const t = todayISO();
        const upcoming = [], past = [], closed = [];
        for (const a of queryFiltered) {
            if (a.status === "cancelled" || a.status === "no_show" || a.status === "rescheduled") {
                closed.push(a);
                continue;
            }
            // Upcoming includes today's active appointments (so we don't lose
            // them when the dedicated Today sub-tab went away).
            if (a.date >= t) upcoming.push(a);
            else past.push(a);
        }
        upcoming.sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
        past.sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
        closed.sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
        return { upcoming, past, closed };
    }, [queryFiltered]);

    /* Today panel still pulls from the unfiltered set so a wayward search
       string doesn't accidentally hide a chair waiting in reception. */
    const todayItems = useMemo(() => {
        const t = todayISO();
        return appts
            .filter((a) => a.date === t)
            .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }, [appts]);

    const filtered = buckets[filter] || [];

    /* ─── Status actions ─────────────────────────────────────── */
    const doAction = async (verb, appt, body) => {
        try {
            const method = verb === "reschedule" ? "post" : "put";
            await api[method](`/doctor/appointments/${appt.id}/${verb}`, body || undefined);
            // iter101.93 — Auto-billing removed. No more "invoice auto-issued"
            // toast; if the doctor wants to charge a no-show / late-cancel
            // fee, they do it manually from the patient's Billing tab.
            if (verb === "restore") {
                toast.success("Appointment restored to confirmed");
            } else {
                toast.success(`Appointment ${verb === "no-show" ? "marked no-show" : verb + "d"}`);
            }
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Action failed");
        }
    };

    const whatsappAppt = (appt) => {
        const url = buildAppointmentWhatsappLink(appt, {
            name: user?.doctor_clinic_name || user?.clinic_name,
            address: user?.doctor_clinic_address || user?.address,
        });
        if (!url) { toast.error("Patient has no phone number."); return; }
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div data-testid="doctor-appointments-page" className="space-y-5 -mt-2">
            {/* ── Page header ── matches /doctor/cases + /doctor/patients */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">My Appointments</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Your chairside schedule — book, check patients in, and reschedule without leaving the page.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                        title="Patient is already in the clinic. Logs an appointment dated now and marks them as arrived."
                        onClick={() => setOpenWalkIn(true)}
                        data-testid="walk-in-btn"
                    >
                        <Lightning size={13} className="mr-1" weight="duotone" /> Walk-in
                    </Button>
                    <Button
                        size="sm"
                        className="rounded-full"
                        title="Schedule a future appointment — pick patient, date, time, type and clinic."
                        onClick={() => setOpenBook(true)}
                        data-testid="book-appt-btn"
                    >
                        <Plus size={14} className="mr-1" weight="bold" /> New Appointment
                    </Button>
                </div>
            </div>

            {/* ── 5-card stat strip ── no left-rail accents */}
            <AppointmentsStatStrip appts={appts} buckets={buckets} />

            {/* Patient-initiated reschedule requests — iter 58 inbox panel.
                Renders nothing when there are no pending requests, so the
                page stays untouched in the steady state. When patients ask
                for a reschedule, this is impossible to miss above the
                calendar. `onResolved` triggers a refresh of the underlying
                appointment list so the newly-rescheduled visit appears. */}
            <RescheduleRequestsInbox onResolved={() => load && load()} />


            {/* PRIMARY CONTROLS — iter100.197 · Shared FilterSearchBar
                primitive locks height/font/placeholder styling. List
                sub-filters render as a FilterChipRow below. */}
            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search patient, phone, type…"
                searchTestid="appt-search"
                testid="appt-filter-bar"
                rightSlot={
                    <>
                        <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9" data-testid="appt-view-toggle">
                            {[
                                { key: "list",  label: "List",  icon: ListBullets },
                                { key: "day",   label: "Day",   icon: CalendarBlank },
                                { key: "week",  label: "Week",  icon: CalendarBlank },
                                { key: "month", label: "Month", icon: CalendarBlank },
                            ].map((v) => {
                                const Icon = v.icon;
                                const active = view === v.key;
                                return (
                                    <button
                                        key={v.key}
                                        onClick={() => setView(v.key)}
                                        data-testid={`view-${v.key}`}
                                        className={`px-2.5 h-full inline-flex items-center gap-1 text-[12px] font-medium transition ${active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                        title={`${v.label} view`}
                                    >
                                        <Icon size={13} weight={active ? "duotone" : "regular"} /> {v.label}
                                    </button>
                                );
                            })}
                        </div>
                        {view !== "list" && (
                            <CalendarNav
                                view={view}
                                date={dayDate}
                                onDate={setDayDate}
                            />
                        )}
                    </>
                }
            />

            {/* List sub-filter chips (row 2). Hidden in calendar views
                since they only apply to the list bucket. */}
            {view === "list" && (
                <FilterChipRow
                    value={filter}
                    onChange={setFilter}
                    testid="appt-tabbar"
                    chipTestidPrefix="filter"
                    chips={[
                        { id: "upcoming", label: "Upcoming",             count: buckets.upcoming.length, tone: "primary" },
                        { id: "past",     label: "Past",                 count: buckets.past.length,     tone: "slate"   },
                        { id: "closed",   label: "Cancelled · no-shows", count: buckets.closed.length,   tone: "rose"    },
                    ]}
                />
            )}

            {view === "day" ? (
                <DayGridView
                    date={dayDate}
                    appts={queryFiltered.filter((a) => a.date === dayDate)}
                    onOpen={(a) => setPreviewAppt(a)}
                    onDragReschedule={(payload) => setPendingDrag(payload)}
                />
            ) : view === "week" ? (
                <WeekGridView
                    weekStart={startOfWeek(dayDate)}
                    appts={queryFiltered}
                    onOpen={(a) => setPreviewAppt(a)}
                    onDragReschedule={(payload) => setPendingDrag(payload)}
                />
            ) : view === "month" ? (
                <MonthGridView
                    monthOf={dayDate}
                    appts={queryFiltered}
                    onPickDay={(d) => { setDayDate(d); setView("day"); }}
                />
            ) : (
              <>
                {/* LIST — three uniquely-valuable lenses, switched via the
                    filter pills in the primary controls row above.
                    Today panel still pinned to the top of Upcoming. */}
                {loading ? (
                    <div className="text-center text-muted-foreground py-16 text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="appt-empty">
                        {filter === "upcoming" ? "No upcoming appointments scheduled." :
                         filter === "past" ? "No past appointments yet." :
                         filter === "closed" ? "No cancelled or no-show appointments." : "Nothing here yet."}
                    </div>
                ) : (
                    <DayGroupedAppointmentList
                        appts={filtered}
                        filter={filter}
                        onPreview={setPreviewAppt}
                        onArrive={(a) => doAction("arrive", a)}
                        onStart={(a) => doAction("start", a)}
                        onComplete={(a) => doAction("complete", a)}
                        onWhatsApp={whatsappAppt}
                        onReschedule={setRescheduleTarget}
                        onCancel={setCancelTarget}
                        onNoShow={(a) => doAction("no-show", a)}
                        onRestore={(a) => doAction("restore", a)}
                        onJumpToDay={() => { setDayDate(todayISO()); setView("day"); }}
                    />
                )}
              </>
            )}

            {/* DIALOGS */}
            <BookingDialog
                open={openBook}
                onClose={() => setOpenBook(false)}
                onCreated={load}
                doctorId={user?.doctor_id}
            />
            <WalkInDialog
                open={openWalkIn}
                onClose={() => setOpenWalkIn(false)}
                onCreated={load}
            />
            <CancelDialog
                appt={cancelTarget}
                onClose={() => setCancelTarget(null)}
                onConfirm={async (reason) => {
                    await doAction("cancel", cancelTarget, { reason });
                    setCancelTarget(null);
                }}
            />
            <RescheduleDialog
                appt={rescheduleTarget}
                onClose={() => setRescheduleTarget(null)}
                onConfirm={async (payload) => {
                    await doAction("reschedule", rescheduleTarget, payload);
                    setRescheduleTarget(null);
                }}
            />
            {/* Drag-to-reschedule funnel: a drop on a new slot lands here
                pre-filled. Reuses the regular reschedule dialog so the
                receptionist must explicitly confirm — protects against
                accidental drags. */}
            <RescheduleDialog
                appt={pendingDrag ? { ...pendingDrag.appt, date: pendingDrag.date, time: pendingDrag.time } : null}
                onClose={() => setPendingDrag(null)}
                onConfirm={async (payload) => {
                    if (pendingDrag) {
                        await doAction("reschedule", pendingDrag.appt, payload);
                        setPendingDrag(null);
                    }
                }}
            />
            {/* Appointment preview — centered modal popup (matches My Patients
                pattern). Opens when a row is clicked anywhere in the body. */}
            <AppointmentPreviewModal
                appt={previewAppt}
                onClose={() => setPreviewAppt(null)}
                onArrive={() => { doAction("arrive", previewAppt); setPreviewAppt(null); }}
                onComplete={() => { doAction("complete", previewAppt); setPreviewAppt(null); }}
                onWhatsApp={() => { whatsappAppt(previewAppt); }}
                onReschedule={() => { setRescheduleTarget(previewAppt); setPreviewAppt(null); }}
                onCancel={() => { setCancelTarget(previewAppt); setPreviewAppt(null); }}
            />
        </div>
    );
}

/* ───────────────────── Today panel ───────────────────── */

function TodayStrip({ items, onClick }) {
    // Single-line summary bar. Goal: give the receptionist a glance at
    // today's volume + the next appointment without consuming vertical
    // space that calendar grids need to be above the fold.
    const now = new Date();
    const currentMin = now.getHours() * 60 + now.getMinutes();
    const active = items.filter((a) => ACTIVE_STATES.has(a.status));
    const next = active.find((a) => {
        const [h, m] = (a.time || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
        return h * 60 + m >= currentMin;
    });
    const completed = items.filter((a) => a.status === "completed").length;

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid="today-panel"
            className="w-full rounded-xl border border-border bg-card hover:border-primary/40 transition-colors px-4 py-2.5 flex items-center justify-between gap-4 text-left"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <Calendar size={14} weight="duotone" />
                </div>
                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Today</span>
                    <span className="text-sm font-semibold text-foreground">
                        {items.length === 0
                            ? "Nothing scheduled"
                            : `${items.length} appointment${items.length === 1 ? "" : "s"}`}
                    </span>
                    {completed > 0 && (
                        <span className="text-xs text-muted-foreground">· {completed} completed</span>
                    )}
                    {next && (
                        <span className="text-xs text-muted-foreground truncate">
                            · Up next <span className="font-mono font-semibold text-foreground">{next.time}</span> {next.patient_name || "Patient"}
                        </span>
                    )}
                </div>
            </div>
            <div className="text-[11px] font-semibold text-primary shrink-0">
                Open Day view →
            </div>
        </button>
    );
}

/* ───────────────────── Single appointment row ───────────────────── */

function AppointmentRow({
    appt, compact, onPreview, onArrive, onStart, onComplete, onWhatsApp, onReschedule, onCancel, onNoShow, onRestore,
}) {
    const a = appt;
    const status = a.status || "scheduled";
    const style = STATUS_STYLE[status] || STATUS_STYLE.scheduled;
    const type = a.appointment_type || a.type || "consultation";
    const duration = a.duration_minutes || 30;
    const isActive = ACTIVE_STATES.has(status);
    const isCancelled = status === "cancelled" || status === "no_show" || status === "rescheduled";
    const noShowCount = a.patient_no_show_count || 0;
    // iter101.93 — fee_invoice_id / fee_amount tracking removed (no more
    // auto-billing); doctor issues any fee invoice manually.
    // Click anywhere on the row body opens the preview popup. Action buttons
    // are wrapped in a stopPropagation handler below so they don't trigger.
    const handleRowClick = () => { if (onPreview) onPreview(a); };

    return (
        <div
            data-testid={`doctor-appt-${a.id}`}
            onClick={handleRowClick}
            role={onPreview ? "button" : undefined}
            tabIndex={onPreview ? 0 : undefined}
            onKeyDown={(e) => { if (onPreview && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onPreview(a); } }}
            className={`${compact ? "px-5 py-4" : "bg-card border border-border rounded-xl overflow-hidden hover:shadow-md hover:border-primary/30"} flex flex-col sm:flex-row sm:items-center gap-0 cursor-pointer transition-all`}
        >
            {/* Time column — clean side panel (NO left-rail accent per design rule) */}
            <div className="sm:w-20 shrink-0 flex sm:flex-col items-center justify-center gap-1 sm:gap-1 px-4 py-3 sm:py-4 bg-secondary/40 sm:border-r border-border">
                <div className="font-display text-lg font-bold text-foreground leading-none">{a.time}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    {a.date === todayISO() ? "today" : a.date}
                </div>
                <div className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{duration} min</div>
            </div>

            {/* Patient + type */}
            <div className="flex-1 min-w-0 px-4 py-3 sm:py-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-[14px] font-semibold truncate">{a.patient_name || "(unnamed)"}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{humanPatientId(a.patient_id)}</span>
                    {a.walk_in && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5">
                            walk-in
                        </span>
                    )}
                    {a.source === "patient_self" && !a.walk_in && (
                        <span
                            className="text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 rounded-full px-1.5 py-0.5"
                            title="Self-booked from Invisique™ website"
                            data-testid={`source-web-${a.id}`}
                        >
                            web
                        </span>
                    )}
                    {a.rescheduled_from_id && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 rounded-full px-1.5 py-0.5">
                            rescheduled
                        </span>
                    )}
                    {noShowCount > 0 && isActive && (
                        <span
                            className="text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 rounded-full px-1.5 py-0.5 inline-flex items-center gap-1"
                            title={`Patient has ${noShowCount} prior no-show${noShowCount > 1 ? "s" : ""}`}
                            data-testid={`no-show-chip-${a.id}`}
                        >
                            <Warning size={10} weight="fill" /> {noShowCount} prior no-show{noShowCount > 1 ? "s" : ""}
                        </span>
                    )}
                    {a.auto_no_show && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 rounded-full px-1.5 py-0.5">
                            auto
                        </span>
                    )}
                    {/* iter101.93 — "late cancel" chip removed. It was
                        a signal for the deprecated auto-billing path. */}
                </div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Phone size={11} /> {a.patient_phone || "—"}</span>
                    <span className="inline-flex items-center gap-1"><ListChecks size={11} /> {typeLabel(type)}</span>
                    {a.practice_location_name && (
                        <span className="inline-flex items-center gap-1"><MapPin size={11} /> {a.practice_location_name}</span>
                    )}
                </div>
                {(a.notes || a.staff_notes) && (
                    <div className="text-[11px] text-muted-foreground italic mt-1.5 truncate">
                        {a.staff_notes
                            ? <><span className="font-semibold text-amber-700 not-italic">[desk] </span>{a.staff_notes}</>
                            : <>
                                {/* iter98.37 — Distinguish patient-self notes from
                                    staff notes so the doctor can scan the agenda
                                    and immediately see the patient's own ask
                                    versus the front-desk's internal scribble. */}
                                {(a.source === "patient_self" || a.source === "self") && (
                                    <span className="font-semibold text-primary not-italic">[from patient] </span>
                                )}{a.notes}
                            </>
                        }
                    </div>
                )}
                {a.cancellation_reason && (
                    <div className="text-[11px] text-rose-600 mt-1">Cancelled: {a.cancellation_reason}</div>
                )}
                {/* iter101.93 — "Fee invoice auto-issued" strip removed;
                    no more auto-billing. */}
            </div>

            {/* Status + actions */}
            <div
                className="flex items-center gap-1.5 flex-wrap justify-end shrink-0 px-4 py-3 sm:py-4"
                onClick={(e) => e.stopPropagation()}
            >
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${style.cls}`}>
                    {style.label}
                </span>

                {isActive && (
                    <>
                        {(status === "scheduled" || status === "pending" || status === "confirmed") && (
                            <Button size="sm" variant="outline" onClick={onArrive} data-testid={`arrive-${a.id}`} title="Mark arrived" className="rounded-md h-8 px-2.5 text-xs">
                                <UserPlus size={13} className="mr-1" /> Arrived
                            </Button>
                        )}
                        {status === "arrived" && (
                            <Button size="sm" variant="outline" onClick={onStart} data-testid={`start-${a.id}`} title="Start visit" className="rounded-md h-8 px-2.5 text-xs">
                                <Clock size={13} className="mr-1" /> Start
                            </Button>
                        )}
                        {(status === "arrived" || status === "in_progress") && (
                            <Button size="sm" variant="outline" onClick={onComplete} data-testid={`complete-${a.id}`} className="rounded-md h-8 px-2.5 text-xs border-primary/30 text-primary hover:bg-primary/10">
                                <CheckCircle size={13} className="mr-1" /> Complete
                            </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={onReschedule} data-testid={`reschedule-${a.id}`} title="Reschedule" className="rounded-md w-8 h-8 p-0">
                            <ArrowsClockwise size={13} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={onWhatsApp} className="rounded-md w-8 h-8 p-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`appt-whatsapp-${a.id}`} title="WhatsApp">
                            <WhatsappLogo size={13} weight="duotone" />
                        </Button>
                        {(status === "scheduled" || status === "pending" || status === "confirmed") && (
                            <Button size="sm" variant="ghost" onClick={onNoShow} data-testid={`no-show-${a.id}`} className="rounded-md w-8 h-8 p-0 text-rose-600 hover:bg-rose-50" title="Mark no-show">
                                <Warning size={13} />
                            </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`cancel-${a.id}`} className="rounded-md w-8 h-8 p-0 text-rose-600 hover:bg-rose-50" title="Cancel">
                            <XCircle size={13} />
                        </Button>
                    </>
                )}

                {!isActive && !isCancelled && (
                    <Button size="sm" variant="outline" onClick={onWhatsApp} className="rounded-md w-8 h-8 p-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`appt-whatsapp-${a.id}`}>
                        <WhatsappLogo size={13} weight="duotone" />
                    </Button>
                )}
                {status === "no_show" && a.auto_no_show && onRestore && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onRestore}
                        className="rounded-md h-8 px-2.5 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                        data-testid={`restore-${a.id}`}
                        title="Restore to confirmed (patient turned up late)"
                    >
                        <ArrowUUpLeft size={13} className="mr-1" /> Restore
                    </Button>
                )}
            </div>
        </div>
    );
}

/* ───────────────────── Booking dialog ───────────────────── */

function BookingDialog({ open, onClose, onCreated, doctorId }) {
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [date, setDate] = useState(todayISO());
    /* iter98.99m — Time defaults to the next available quarter-hour
     * slot. On a 7pm open the picker initialises at 19:15 rather than
     * a stale 10:00 that would silently fail server-side validation. */
    const [time, setTime] = useState(() => nextBookableTime(todayISO()));
    const [type, setType] = useState("consultation");
    const [durationOverride, setDurationOverride] = useState("");
    /* iter98.99m — `notes` is no longer rendered in the UI (the
     * patient-facing notes field was removed per user direction). The
     * state stays so the API contract is unchanged; the value is always
     * an empty string for new bookings made through this dialog. */
    const [notes, setNotes] = useState("");
    const [staffNotes, setStaffNotes] = useState("");
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState("");
    const [busy, setBusy] = useState(false);
    const [matched, setMatched] = useState(null);

    useEffect(() => {
        if (!open) return;
        const freshDate = todayISO();
        setPhone(""); setName(""); setDate(freshDate); setTime(nextBookableTime(freshDate));
        setType("consultation"); setDurationOverride(""); setNotes(""); setStaffNotes("");
        setMatched(null);
        api.get("/doctor/practice-locations")
            .then((r) => {
                setLocations(r.data || []);
                const primary = (r.data || []).find((l) => l.is_primary) || (r.data || [])[0];
                if (primary) setLocationId(primary.id);
            })
            .catch(() => {});
    }, [open, doctorId]);

    /* iter98.99m — When the doctor switches dates, ensure the time
     * doesn't fall in the past. The picker's `min` attribute already
     * blocks browser-native scrubbing, but if the user had a stale time
     * in state we re-snap to the next quarter-hour from now. */
    useEffect(() => {
        if (!date) return;
        if (date === todayISO()) {
            const earliest = nextBookableTime(date);
            if (time < earliest) setTime(earliest);
        }
    }, [date, time]);

    // Match existing patient by phone on debounce-ish blur
    const checkPhone = async () => {
        if (!isValid10Digit(phone)) { setMatched(null); return; }
        try {
            const e164 = toE164(phone);
            const r = await api.get("/doctor/patients", { params: { q: phone } });
            const m = (r.data || []).find((p) => (p.phone || "").replace(/\s/g, "") === e164);
            setMatched(m || null);
            if (m && !name) setName(m.name || "");
        } catch {
            setMatched(null);
        }
    };

    const submit = async () => {
        if (!isValid10Digit(phone) && !matched) { toast.error("Enter the patient's 10-digit mobile number"); return; }
        if (!date || !time) { toast.error("Date and time required"); return; }
        /* iter98.99m — Belt-and-braces guard against past-time bookings
         * for today (e.g. the doctor manually typed "10:00" at 7pm).
         * `<input type="time" min=...>` already blocks via the picker,
         * but pasted / typed values can slip through on some browsers. */
        if (date === todayISO() && time < nowHHMM()) {
            toast.error("Time has already passed — pick a future slot");
            return;
        }
        setBusy(true);
        try {
            const body = {
                patient_id: matched?.id,
                patient_phone: toE164(phone),
                patient_name: name.trim(),
                date, time,
                appointment_type: type,
                duration_minutes: durationOverride ? parseInt(durationOverride, 10) : undefined,
                practice_location_id: locationId || undefined,
                notes: notes.trim(),
                staff_notes: staffNotes.trim(),
                source: "phone",
            };
            await api.post("/doctor/appointments", body);
            toast.success("Appointment booked");
            onCreated?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Booking failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden">
                {/* ── Premium header · iter102.07 ─────────────────────────
                    Editorial title band with a subtle teal wash + icon
                    accent, matching the CommandHeaderShell rhythm used on
                    the CaseDetail pages. Compacted for laptop viewports
                    in iter102.08. */}
                <div className="relative px-5 pt-4 pb-3 border-b border-border bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-transparent">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-primary/12 grid place-items-center shrink-0">
                            <CalendarBlank size={17} weight="duotone" className="text-primary" />
                        </div>
                        <DialogHeader className="flex-1 min-w-0 text-left space-y-0.5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary/80">
                                Front-desk booking
                            </div>
                            <DialogTitle className="text-[17px] font-bold leading-tight">
                                New Appointment
                            </DialogTitle>
                            <DialogDescription className="text-[11.5px] text-muted-foreground leading-snug">
                                Type the patient’s phone — we’ll match an existing record or create a lite profile they can claim later.
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                {/* ── Scrollable body ── */}
                <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Section 1 · Patient */}
                    <section className="space-y-2.5">
                        <SectionEyebrow icon={User} label="Patient" />
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Phone <span className="text-primary">*</span></Label>
                                <div className="mt-1">
                                    <PhoneInput10
                                        value={phone}
                                        onChange={setPhone}
                                        onBlur={checkPhone}
                                        inputClassName="h-9"
                                        testid="book-phone"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Patient name</Label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Full name"
                                    className="h-9 mt-1"
                                    data-testid="book-name"
                                />
                            </div>
                        </div>
                        {matched && (
                            <div
                                className="mt-1 flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-emerald-50/60 border border-emerald-200"
                                data-testid="book-matched-banner"
                            >
                                <div className="w-7 h-7 rounded-full bg-emerald-600 text-white grid place-items-center text-[10.5px] font-bold shrink-0">
                                    {(matched.name || "?").split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-emerald-900 truncate">
                                        Matched · {matched.name || matched.phone}
                                    </div>
                                    {(matched.no_show_count || 0) > 0 && (
                                        <div className="text-[10.5px] text-rose-700 inline-flex items-center gap-1 font-medium" data-testid="book-no-show-warning">
                                            <Warning size={10} weight="fill" /> {matched.no_show_count} prior no-show{matched.no_show_count > 1 ? "s" : ""}
                                            {(matched.cancellation_count || 0) > 0 ? ` · ${matched.cancellation_count} cancellation${matched.cancellation_count > 1 ? "s" : ""}` : ""}
                                        </div>
                                    )}
                                </div>
                                <CheckCircle size={15} weight="fill" className="text-emerald-600 shrink-0" />
                            </div>
                        )}
                        {!matched && isValid10Digit(phone) && (
                            <div className="mt-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-primary/[0.04] border border-dashed border-primary/25 text-[11px] text-primary/80">
                                <UserPlus size={12} weight="duotone" className="text-primary shrink-0" />
                                <span>New patient — we’ll create a lite profile they can claim later.</span>
                            </div>
                        )}
                    </section>

                    {/* Section 2 · Visit type */}
                    <section className="space-y-1.5">
                        <SectionEyebrow icon={Stethoscope} label="Visit type" />
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="h-9" data-testid="book-type">
                                <SelectValue placeholder="Visit type" />
                            </SelectTrigger>
                            <SelectContent>
                                {APPT_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </section>

                    {/* Section 3 · When */}
                    <section className="space-y-2.5">
                        <SectionEyebrow icon={Clock} label="When" />
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Date <span className="text-primary">*</span></Label>
                                <Input type="date" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} className="h-9 mt-1" data-testid="book-date" />
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">
                                    Time <span className="text-primary">*</span>
                                    <span className="text-muted-foreground italic font-normal ml-1">· 15-min slots</span>
                                </Label>
                                <div className="mt-1">
                                    <TimeSlotSelect
                                        value={time}
                                        onChange={setTime}
                                        date={date}
                                        testId="book-time"
                                        emptyLabel="Fully booked today · Jump to tomorrow"
                                        onEmptyJump={() => {
                                            const d = new Date(`${date}T00:00:00`);
                                            d.setDate(d.getDate() + 1);
                                            const y = d.getFullYear();
                                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                                            const dd = String(d.getDate()).padStart(2, "0");
                                            setDate(`${y}-${mm}-${dd}`);
                                            setTime("09:00");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Section 4 · Location (only when doctor has multiple) */}
                    {locations.length > 1 && (
                        <section className="space-y-1.5">
                            <SectionEyebrow icon={MapPin} label="Practice location" />
                            <Select value={locationId} onValueChange={setLocationId}>
                                <SelectTrigger className="h-9" data-testid="book-location">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {locations.map((l) => (
                                        <SelectItem key={l.id} value={l.id}>{l.name || l.clinic_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </section>
                    )}

                    {/* Section 5 · Notes */}
                    <section className="space-y-1.5">
                        <SectionEyebrow
                            icon={ChatCircleDots}
                            label="Front-desk notes"
                            suffix="Internal only"
                        />
                        <Textarea
                            value={staffNotes}
                            onChange={(e) => setStaffNotes(e.target.value)}
                            rows={2}
                            placeholder="e.g. Knee surgery — please seat them quickly."
                            data-testid="book-staff-notes"
                            className="resize-none text-[13px]"
                        />
                    </section>
                </div>

                {/* ── Footer — primary CTA visually elevated ── */}
                <DialogFooter className="px-5 py-3 border-t border-border bg-secondary/20 gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} className="h-9 text-muted-foreground hover:text-foreground">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="rounded-full px-5 h-9 bg-primary hover:bg-primary/90 shadow-sm hover:shadow-md transition-shadow font-semibold text-[13px]"
                        data-testid="book-submit"
                    >
                        {busy ? "Saving…" : "Save Appointment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* SectionEyebrow — a small reusable header used inside the premium
   BookingDialog / WalkInDialog / CancelDialog to group fields. Icon
   disc + uppercase caps + optional italic suffix. Supports three
   tones: "primary" (teal), "amber" (walk-in / urgency), and "rose"
   (destructive / cancellation). */
function SectionEyebrow({ icon: Icon, label, suffix = null, tone = "primary" }) {
    const toneMap = {
        primary: { disc: "bg-primary/10", icon: "text-primary" },
        amber: { disc: "bg-amber-100", icon: "text-amber-700" },
        rose: { disc: "bg-rose-100", icon: "text-rose-700" },
    };
    const toneCls = toneMap[tone] || toneMap.primary;
    return (
        <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-md ${toneCls.disc} grid place-items-center shrink-0`}>
                <Icon size={11} weight="duotone" className={toneCls.icon} />
            </div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-foreground/80">
                {label}
                {suffix && (
                    <span className="text-muted-foreground font-normal italic normal-case tracking-normal ml-1.5">
                        · {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ───────────────────── Walk-in dialog ─────────────────────
   Quick-add for a patient who has just turned up at the front desk.
   Replaces the previous double `window.prompt` flow so reception can:
     - confirm whether the phone matches an existing patient
     - capture the visit type (consultation / scan / adjustment …)
     - pick the practice location (single-branch clinics skip this)
     - jot a one-liner front-desk note (e.g. "lost retainer")
   Creates the appointment dated NOW and immediately marks it `arrived`. */
function WalkInDialog({ open, onClose, onCreated }) {
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState("consultation");
    const [staffNotes, setStaffNotes] = useState("");
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState("");
    const [matched, setMatched] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setPhone(""); setName(""); setType("consultation"); setStaffNotes(""); setMatched(null);
        api.get("/doctor/practice-locations")
            .then((r) => {
                setLocations(r.data || []);
                const primary = (r.data || []).find((l) => l.is_primary) || (r.data || [])[0];
                if (primary) setLocationId(primary.id);
            })
            .catch(() => {});
    }, [open]);

    const checkPhone = async () => {
        if (!isValid10Digit(phone)) { setMatched(null); return; }
        try {
            const e164 = toE164(phone);
            const r = await api.get("/doctor/patients", { params: { q: phone } });
            const m = (r.data || []).find((p) => (p.phone || "").replace(/\s/g, "") === e164);
            setMatched(m || null);
            if (m && !name) setName(m.name || "");
        } catch {
            setMatched(null);
        }
    };

    const submit = async () => {
        if (!isValid10Digit(phone) && !matched) { toast.error("Enter the patient's 10-digit mobile number"); return; }
        setBusy(true);
        try {
            await api.post("/doctor/appointments/walk-in", {
                patient_id: matched?.id,
                patient_phone: toE164(phone),
                patient_name: name.trim(),
                appointment_type: type,
                practice_location_id: locationId || undefined,
                staff_notes: staffNotes.trim(),
            });
            toast.success("Walk-in logged — patient marked arrived");
            onCreated?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Walk-in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="walk-in-dialog">
                {/* ── Premium header · iter102.08 ─────────────────────────
                    Amber wash + Lightning icon signals the urgent
                    "already at the desk" tone. Compact for laptop screens. */}
                <div className="relative px-5 pt-4 pb-3 border-b border-border bg-gradient-to-br from-amber-100/70 via-amber-50/40 to-transparent">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-amber-500/15 grid place-items-center shrink-0">
                            <Lightning size={17} weight="duotone" className="text-amber-700" />
                        </div>
                        <DialogHeader className="flex-1 min-w-0 text-left space-y-0.5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800">
                                Front-desk · Walk-in
                            </div>
                            <DialogTitle className="text-[17px] font-bold leading-tight">
                                Log a walk-in
                            </DialogTitle>
                            <DialogDescription className="text-[11.5px] text-muted-foreground leading-snug">
                                Patient is already at your desk. We’ll create the appointment now and mark them <span className="font-semibold text-amber-800">arrived</span> so the chair picks them up right away.
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                {/* ── Scrollable body ── */}
                <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Section 1 · Patient */}
                    <section className="space-y-2.5">
                        <SectionEyebrow icon={User} label="Patient" tone="amber" />
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Phone <span className="text-amber-700">*</span></Label>
                                <div className="mt-1">
                                    <PhoneInput10
                                        value={phone}
                                        onChange={setPhone}
                                        onBlur={checkPhone}
                                        inputClassName="h-9"
                                        testid="walk-in-phone"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Patient name</Label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Full name"
                                    className="h-9 mt-1"
                                    data-testid="walk-in-name"
                                />
                            </div>
                        </div>
                        {matched && (
                            <div className="mt-1 flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-emerald-50/60 border border-emerald-200" data-testid="walk-in-matched-banner">
                                <div className="w-7 h-7 rounded-full bg-emerald-600 text-white grid place-items-center text-[10.5px] font-bold shrink-0">
                                    {(matched.name || "?").split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-emerald-900 truncate">
                                        Matched · {matched.name || matched.phone}
                                    </div>
                                    {(matched.no_show_count || 0) > 0 && (
                                        <div className="text-[10.5px] text-rose-700 inline-flex items-center gap-1 font-medium" data-testid="walk-in-no-show-warning">
                                            <Warning size={10} weight="fill" /> {matched.no_show_count} prior no-show{matched.no_show_count > 1 ? "s" : ""}
                                        </div>
                                    )}
                                </div>
                                <CheckCircle size={15} weight="fill" className="text-emerald-600 shrink-0" />
                            </div>
                        )}
                        {!matched && isValid10Digit(phone) && (
                            <div className="mt-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-dashed border-amber-300 text-[11px] text-amber-800">
                                <UserPlus size={12} weight="duotone" className="text-amber-700 shrink-0" />
                                <span>New patient — we’ll create a lite profile they can claim later.</span>
                            </div>
                        )}
                    </section>

                    {/* Section 2 · Visit type + optional Location on same row */}
                    <section className="space-y-1.5">
                        <SectionEyebrow icon={Stethoscope} label="Visit" tone="amber" />
                        <div className={`grid ${locations.length > 1 ? "sm:grid-cols-2" : ""} gap-2.5`}>
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Type <span className="text-amber-700">*</span></Label>
                                <Select value={type} onValueChange={setType}>
                                    <SelectTrigger className="h-9 mt-1" data-testid="walk-in-type">
                                        <SelectValue placeholder="Visit type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {APPT_TYPES.map((t) => (
                                            <SelectItem key={t.value} value={t.value}>
                                                {t.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {locations.length > 1 && (
                                <div>
                                    <Label className="text-[11px] font-semibold text-foreground/80">
                                        <MapPin size={10} weight="duotone" className="inline mr-1 text-amber-700" />
                                        Practice location
                                    </Label>
                                    <Select value={locationId} onValueChange={setLocationId}>
                                        <SelectTrigger className="h-9 mt-1" data-testid="walk-in-location">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map((l) => (
                                                <SelectItem key={l.id} value={l.id}>{l.name || l.clinic_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Section 3 · Notes */}
                    <section className="space-y-1.5">
                        <SectionEyebrow
                            icon={ChatCircleDots}
                            label="Front-desk notes"
                            suffix="Internal only"
                            tone="amber"
                        />
                        <Textarea
                            value={staffNotes}
                            onChange={(e) => setStaffNotes(e.target.value)}
                            rows={2}
                            placeholder="e.g. lost retainer, complaining of soreness on UR3."
                            data-testid="walk-in-staff-notes"
                            className="resize-none text-[13px]"
                        />
                    </section>
                </div>

                {/* ── Footer — amber CTA ── */}
                <DialogFooter className="px-5 py-3 border-t border-border bg-secondary/20 gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} className="h-9 text-muted-foreground hover:text-foreground">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="rounded-full px-5 h-9 bg-amber-600 hover:bg-amber-700 text-white shadow-sm hover:shadow-md transition-shadow font-semibold text-[13px]"
                        data-testid="walk-in-submit"
                    >
                        <Lightning size={13} className="mr-1.5" weight="duotone" />
                        {busy ? "Logging…" : "Log walk-in"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ───────────────────── Cancel dialog ─────────────────────
   iter102.10 — Premium redesign with rose accent. Header eyebrow
   signals the destructive intent; a summary card echoes back the
   patient / date / time so the doctor can double-check before
   confirming; reason textarea sits inside a sectioned block; two-step
   footer keeps "Keep appointment" as the safe default. */

function CancelDialog({ appt, onClose, onConfirm }) {
    const [reason, setReason] = useState("");
    // iter101.93 — Fee preview removed. Cancelling is a pure status
    // change now; any late-cancel fee is billed manually by the doctor
    // from the patient's Billing tab (their choice).
    useEffect(() => {
        setReason("");
    }, [appt?.id]);
    if (!appt) return null;

    /* Pretty-print the appt.date (ISO yyyy-mm-dd) → e.g. "Thu, 13 Aug".
       We keep the time as-is (HH:MM) because it's already user-friendly. */
    const prettyDate = (() => {
        try {
            const d = new Date(`${appt.date}T00:00:00`);
            if (Number.isNaN(d.getTime())) return appt.date;
            return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
        } catch { return appt.date; }
    })();
    const prettyTime = fmtSlotHuman(appt.time) || appt.time;
    const initials = (appt.patient_name || "?").split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

    return (
        <Dialog open={!!appt} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
                {/* ── Rose-toned header band ── */}
                <div className="relative px-5 pt-4 pb-3 border-b border-border bg-gradient-to-br from-rose-100/70 via-rose-50/40 to-transparent">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-rose-500/15 grid place-items-center shrink-0">
                            <XCircle size={17} weight="duotone" className="text-rose-700" />
                        </div>
                        <DialogHeader className="flex-1 min-w-0 text-left space-y-0.5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-rose-800">
                                Appointment · Cancel
                            </div>
                            <DialogTitle className="text-[17px] font-bold leading-tight">
                                Cancel this appointment?
                            </DialogTitle>
                            <DialogDescription className="text-[11.5px] text-muted-foreground leading-snug">
                                We&apos;ll notify {appt.patient_name || "the patient"} on their portal — please jot a short reason.
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="px-5 py-4 space-y-4">
                    {/* Appointment recap card */}
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-rose-50/60 border border-rose-200/70" data-testid="cancel-appt-recap">
                        <div className="w-9 h-9 rounded-full bg-rose-600 text-white grid place-items-center text-[11px] font-bold shrink-0">
                            {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-rose-950 truncate">
                                {appt.patient_name || "Unnamed patient"}
                            </div>
                            <div className="text-[11px] text-rose-800/90 inline-flex items-center gap-1.5 flex-wrap">
                                <CalendarBlank size={11} weight="duotone" />
                                {prettyDate}
                                <span className="opacity-40">·</span>
                                <Clock size={11} weight="duotone" />
                                {prettyTime}
                            </div>
                        </div>
                    </div>

                    {/* Reason */}
                    <section className="space-y-1.5">
                        <SectionEyebrow
                            icon={ChatCircleDots}
                            label="Reason"
                            suffix="Shared with patient"
                            tone="rose"
                        />
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. Patient called to cancel. Will rebook next week."
                            data-testid="cancel-reason"
                            className="resize-none text-[13px]"
                            autoFocus
                        />
                        <div className="text-[10.5px] text-muted-foreground italic">
                            A short line helps the patient understand the change.
                        </div>
                    </section>
                </div>

                {/* ── Footer — "Keep appointment" is the safe default ── */}
                <DialogFooter className="px-5 py-3 border-t border-border bg-secondary/20 gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-9 text-muted-foreground hover:text-foreground"
                        data-testid="cancel-keep"
                    >
                        Keep appointment
                    </Button>
                    <Button
                        onClick={() => reason.trim() ? onConfirm(reason.trim()) : null}
                        disabled={!reason.trim()}
                        className="rounded-full px-5 h-9 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 disabled:hover:bg-rose-300 text-white shadow-sm hover:shadow-md transition-shadow font-semibold text-[13px]"
                        data-testid="cancel-confirm"
                    >
                        <XCircle size={13} weight="duotone" className="mr-1.5" />
                        Cancel appointment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ───────────────────── Reschedule dialog ─────────────────────
   iter102.11 — Premium redesign with teal accent + old/new
   time-diff strip so the doctor can see at a glance where the
   appointment is being moved to before confirming. */

function RescheduleDialog({ appt, onClose, onConfirm }) {
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [reason, setReason] = useState("");
    useEffect(() => {
        if (appt) { setDate(appt.date); setTime(appt.time); setReason(""); }
    }, [appt?.id]);
    if (!appt) return null;

    /* Pretty-print an ISO yyyy-mm-dd → e.g. "Wed, 19 Aug". */
    const prettyDate = (iso) => {
        try {
            const d = new Date(`${iso}T00:00:00`);
            if (Number.isNaN(d.getTime())) return iso || "—";
            return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
        } catch { return iso || "—"; }
    };

    /* Delta line — human-friendly summary of the move. */
    const deltaLabel = (() => {
        if (!date || !time) return null;
        try {
            const oldD = new Date(`${appt.date}T${appt.time}:00`);
            const newD = new Date(`${date}T${time}:00`);
            if (Number.isNaN(oldD.getTime()) || Number.isNaN(newD.getTime())) return null;
            const diffMs = newD.getTime() - oldD.getTime();
            if (diffMs === 0) return { text: "No change yet", tone: "muted" };
            const abs = Math.abs(diffMs);
            const dir = diffMs > 0 ? "later" : "earlier";
            const days = Math.floor(abs / 86_400_000);
            const hours = Math.floor((abs % 86_400_000) / 3_600_000);
            const mins = Math.floor((abs % 3_600_000) / 60_000);
            const parts = [];
            if (days) parts.push(`${days}d`);
            if (hours) parts.push(`${hours}h`);
            if (mins) parts.push(`${mins}m`);
            if (parts.length === 0) parts.push("just moments");
            return { text: `${parts.join(" ")} ${dir}`, tone: diffMs > 0 ? "later" : "earlier" };
        } catch { return null; }
    })();

    const unchanged = date === appt.date && time === appt.time;

    return (
        <Dialog open={!!appt} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden">
                {/* ── Teal header band ── */}
                <div className="relative px-5 pt-4 pb-3 border-b border-border bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-transparent">
                    <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-primary/12 grid place-items-center shrink-0">
                            <ArrowsClockwise size={17} weight="duotone" className="text-primary" />
                        </div>
                        <DialogHeader className="flex-1 min-w-0 text-left space-y-0.5">
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary/80">
                                Appointment · Reschedule
                            </div>
                            <DialogTitle className="text-[17px] font-bold leading-tight">
                                Reschedule this appointment
                            </DialogTitle>
                            <DialogDescription className="text-[11.5px] text-muted-foreground leading-snug">
                                {appt.patient_name} · {typeLabel(appt.appointment_type || appt.type)}. The original slot closes and a new linked appointment is created.
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Old → New diff strip */}
                    <div className="grid grid-cols-[1fr_auto_1fr] items-stretch rounded-lg border border-border overflow-hidden" data-testid="reschedule-diff-strip">
                        {/* OLD */}
                        <div className="px-3 py-2.5 bg-secondary/40">
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">From</div>
                            <div className="mt-0.5 text-[13px] font-semibold text-foreground/70 line-through decoration-muted-foreground/50 truncate">
                                {prettyDate(appt.date)}
                            </div>
                            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                                <Clock size={10} weight="duotone" /> {fmtSlotHuman(appt.time) || appt.time}
                            </div>
                        </div>
                        {/* Arrow */}
                        <div className="grid place-items-center px-2 bg-secondary/20 border-x border-border">
                            <ArrowRight size={14} weight="bold" className="text-primary" />
                        </div>
                        {/* NEW */}
                        <div className="px-3 py-2.5 bg-primary/[0.05]">
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-primary/80">To</div>
                            <div className="mt-0.5 text-[13px] font-semibold text-foreground truncate">
                                {date ? prettyDate(date) : "Pick a date"}
                            </div>
                            <div className="text-[11px] text-primary/90 inline-flex items-center gap-1">
                                <Clock size={10} weight="duotone" /> {time ? (fmtSlotHuman(time) || time) : "—"}
                            </div>
                        </div>
                        {/* Delta caption (spans full width if we have one) */}
                        {deltaLabel && !unchanged && (
                            <div className="col-span-3 px-3 py-1.5 border-t border-border bg-white text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">
                                <span className={deltaLabel.tone === "later" ? "text-primary" : "text-amber-700"}>
                                    {deltaLabel.text}
                                </span>
                            </div>
                        )}
                        {unchanged && (
                            <div className="col-span-3 px-3 py-1.5 border-t border-border bg-white text-center text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                No change yet · pick a new date or time
                            </div>
                        )}
                    </div>

                    {/* When */}
                    <section className="space-y-2">
                        <SectionEyebrow icon={CalendarBlank} label="New slot" suffix="15-minute grid" />
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Date <span className="text-primary">*</span></Label>
                                <Input
                                    type="date"
                                    value={date}
                                    min={todayISO()}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="h-9 mt-1"
                                    data-testid="reschedule-date"
                                />
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-foreground/80">Time <span className="text-primary">*</span></Label>
                                <div className="mt-1">
                                    <TimeSlotSelect
                                        value={time}
                                        onChange={setTime}
                                        date={date}
                                        testId="reschedule-time"
                                        emptyLabel="No slots remain today · pick another day"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Reason */}
                    <section className="space-y-1.5">
                        <SectionEyebrow
                            icon={ChatCircleDots}
                            label="Reason"
                            suffix="Optional · shared with patient"
                        />
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder="e.g. Patient requested an afternoon slot."
                            data-testid="reschedule-reason"
                            className="resize-none text-[13px]"
                        />
                    </section>
                </div>

                {/* ── Footer ── */}
                <DialogFooter className="px-5 py-3 border-t border-border bg-secondary/20 gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-9 text-muted-foreground hover:text-foreground"
                        data-testid="reschedule-cancel"
                    >
                        Keep original
                    </Button>
                    <Button
                        onClick={() => onConfirm({ date, time, reason: reason.trim() })}
                        disabled={!date || !time || unchanged}
                        className="rounded-full px-5 h-9 bg-primary hover:bg-primary/90 disabled:bg-primary/40 disabled:hover:bg-primary/40 text-white shadow-sm hover:shadow-md transition-shadow font-semibold text-[13px]"
                        data-testid="reschedule-confirm"
                    >
                        <ArrowsClockwise size={13} weight="duotone" className="mr-1.5" />
                        Confirm reschedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ───────────────────── Date helpers (week/month nav) ─────────────────────
   Pure utilities. Week starts on Monday — international convention plus
   most Indian clinics treat Mon as the start of the work week.

   IMPORTANT: all helpers parse and format ISO dates using LOCAL date
   components — NOT `toISOString().slice(0, 10)`. The naive ISO approach
   silently breaks in any timezone with a positive UTC offset (e.g. IST
   UTC+5:30): adding +1 day to "2026-05-27" round-trips back to the
   same date, and subtracting -1 day jumps two days. That was the
   original cause of the forward-arrow not advancing the calendar. */
function isoToLocalDate(isoDate) {
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d);
}
function localDateToIso(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function startOfWeek(isoDate) {
    const d = isoToLocalDate(isoDate);
    const dow = (d.getDay() + 6) % 7; // 0=Mon, 6=Sun
    d.setDate(d.getDate() - dow);
    return localDateToIso(d);
}
function addDays(isoDate, n) {
    const d = isoToLocalDate(isoDate);
    d.setDate(d.getDate() + n);
    return localDateToIso(d);
}
function addMonths(isoDate, n) {
    const d = isoToLocalDate(isoDate);
    d.setMonth(d.getMonth() + n);
    return localDateToIso(d);
}
function monthRangeDays(isoDate) {
    // Returns the array of ISO dates for the visible month grid (6 weeks).
    const d = isoToLocalDate(isoDate);
    d.setDate(1);
    const start = startOfWeek(localDateToIso(d));
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function CalendarNav({ view, date, onDate }) {
    const move = (delta) => {
        if (view === "day") onDate(addDays(date, delta));
        else if (view === "week") onDate(addDays(startOfWeek(date), delta * 7));
        else if (view === "month") onDate(addMonths(date, delta));
    };
    const labelFor = () => {
        if (view === "day") return new Date(date + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        if (view === "week") {
            const s = startOfWeek(date);
            const e = addDays(s, 6);
            return `${new Date(s + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(e + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
        }
        return new Date(date + "T00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" });
    };
    return (
        <div className="flex items-center gap-2" data-testid="calendar-nav">
            <Button size="sm" variant="ghost" onClick={() => move(-1)} data-testid="cal-prev">‹</Button>
            <div className="text-sm font-semibold min-w-[180px] text-center" data-testid="cal-label">{labelFor()}</div>
            <Button size="sm" variant="ghost" onClick={() => move(1)} data-testid="cal-next">›</Button>
            <Button size="sm" variant="ghost" onClick={() => onDate(todayISO())} data-testid="cal-today">Today</Button>
        </div>
    );
}


/* ───────────────────── Day-grid calendar view ─────────────────────
   A simple time-grid (08:00–21:00) with one column per practice location,
   or a single column when the clinic has just one branch. Each appointment
   block is sized by duration_minutes and coloured by status. Click a block
   to open the cancel dialog (most common single-tap action).

   We deliberately keep this read-mostly: rescheduling via drag is out of
   Phase 2 scope; for status changes the receptionist flips back to List view.
*/
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 21;
const SLOT_MINUTES = 15;
const PX_PER_MINUTE = 1.4; // 84px per hour → readable but compact
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const TOTAL_HEIGHT = TOTAL_MINUTES * PX_PER_MINUTE;

function DayGridView({ date, appts, onOpen, onDragReschedule }) {
    const columns = useMemo(() => {
        const byLoc = new Map();
        for (const a of appts) {
            const key = a.practice_location_id || "_default";
            if (!byLoc.has(key)) {
                byLoc.set(key, { id: key, label: a.practice_location_name || "Clinic", items: [] });
            }
            byLoc.get(key).items.push(a);
        }
        return Array.from(byLoc.values()).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    }, [appts]);

    const slotLines = [];
    for (let m = 0; m <= TOTAL_MINUTES; m += SLOT_MINUTES) {
        slotLines.push(m);
    }

    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="day-grid">
            <div className="px-5 py-3.5 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
                        <CalendarBlank size={16} weight="duotone" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Day grid</div>
                        <div className="font-display text-base font-bold leading-tight">
                            {new Date(date + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                        </div>
                    </div>
                </div>
                <div className="text-xs text-muted-foreground" data-testid="day-grid-count">
                    {appts.length === 0 ? "Nothing scheduled" : `${appts.length} ${appts.length === 1 ? "appointment" : "appointments"} · drag to reschedule`}
                </div>
            </div>
            {appts.length === 0 ? (
                <div className="px-5 py-12 text-sm text-center text-muted-foreground">
                    No appointments on this day.
                </div>
            ) : (
                <div className="flex">
                    <HourRail />
                    <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, columns.length)}, minmax(0, 1fr))` }}>
                        {columns.map((col) => (
                            <DayColumn
                                key={col.id}
                                column={col}
                                slotLines={slotLines}
                                date={date}
                                onOpen={onOpen}
                                onDragReschedule={onDragReschedule}
                                multi={columns.length > 1}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function HourRail() {
    return (
        <div
            className="w-14 shrink-0 border-r border-border bg-secondary/30 text-[10px] font-mono text-muted-foreground"
            style={{ height: TOTAL_HEIGHT }}
        >
            {Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }).map((_, i) => {
                const hour = DAY_START_HOUR + i;
                return (
                    <div
                        key={hour}
                        className="flex items-start justify-end pr-2 pt-0.5"
                        style={{ height: 60 * PX_PER_MINUTE }}
                    >
                        {hour.toString().padStart(2, "0")}:00
                    </div>
                );
            })}
        </div>
    );
}

function DayColumn({ column, slotLines, date, onOpen, onDragReschedule, multi, dayLabel }) {
    // Drag preview state — while a block is being dragged over this column,
    // we render a ghost block at the snapped slot so the user can target.
    const [hoverMinutes, setHoverMinutes] = useState(null);

    // Snap a Y-pixel offset into a 15-min boundary minute value.
    const snapMinutes = (clientY, containerRect) => {
        const y = clientY - containerRect.top;
        const minute = Math.max(0, Math.min(TOTAL_MINUTES - SLOT_MINUTES, Math.round(y / PX_PER_MINUTE / SLOT_MINUTES) * SLOT_MINUTES));
        return minute;
    };

    const handleDragOver = (e) => {
        if (!e.dataTransfer.types.includes("application/x-appt-id")) return;
        e.preventDefault();          // accept drop
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        setHoverMinutes(snapMinutes(e.clientY, rect));
    };
    const handleDragLeave = () => setHoverMinutes(null);
    const handleDrop = (e) => {
        e.preventDefault();
        const apptId = e.dataTransfer.getData("application/x-appt-id");
        const apptDate = e.dataTransfer.getData("application/x-appt-date");
        if (!apptId) return;
        const minute = snapMinutes(e.clientY, e.currentTarget.getBoundingClientRect());
        const totalMin = (DAY_START_HOUR * 60) + minute;
        const hh = Math.floor(totalMin / 60).toString().padStart(2, "0");
        const mm = (totalMin % 60).toString().padStart(2, "0");
        setHoverMinutes(null);
        // Don't fire reschedule if dropped on the same date+time (no-op).
        if (apptDate === date && e.dataTransfer.getData("application/x-appt-time") === `${hh}:${mm}`) return;
        const appt = column.items.find((a) => a.id === apptId)
            || { id: apptId, appointment_type: e.dataTransfer.getData("application/x-appt-type"),
                 patient_name: e.dataTransfer.getData("application/x-appt-patient") };
        onDragReschedule?.({ appt, date, time: `${hh}:${mm}` });
    };

    return (
        <div
            className="relative border-r border-border last:border-r-0"
            style={{ height: TOTAL_HEIGHT }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid={`day-col-${date}`}
        >
            {(multi || dayLabel) && (
                <div className="absolute top-0 left-0 right-0 px-3 py-1.5 bg-card/80 backdrop-blur-sm border-b border-border z-10 text-[11px] font-bold text-muted-foreground uppercase tracking-wider truncate">
                    {dayLabel || column.label}
                </div>
            )}
            {slotLines.map((m) => (
                <div
                    key={m}
                    className={`absolute left-0 right-0 ${m % 60 === 0 ? "border-t border-border" : "border-t border-border/40"}`}
                    style={{ top: m * PX_PER_MINUTE }}
                />
            ))}
            {column.items.map((a) => {
                const [h, mm] = (a.time || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
                const startMinutes = (h - DAY_START_HOUR) * 60 + mm;
                if (startMinutes < 0 || startMinutes >= TOTAL_MINUTES) return null;
                const duration = a.duration_minutes || 30;
                const top = startMinutes * PX_PER_MINUTE;
                const height = Math.max(24, duration * PX_PER_MINUTE - 2);
                const status = a.status || "scheduled";
                const style = STATUS_STYLE[status] || STATUS_STYLE.scheduled;
                const canDrag = ACTIVE_STATES.has(status);
                return (
                    <div
                        key={a.id}
                        draggable={canDrag}
                        onDragStart={(e) => {
                            if (!canDrag) { e.preventDefault(); return; }
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("application/x-appt-id", a.id);
                            e.dataTransfer.setData("application/x-appt-date", a.date);
                            e.dataTransfer.setData("application/x-appt-time", a.time);
                            e.dataTransfer.setData("application/x-appt-type", a.appointment_type || a.type || "");
                            e.dataTransfer.setData("application/x-appt-patient", a.patient_name || "");
                        }}
                        onClick={() => onOpen(a)}
                        data-testid={`day-block-${a.id}`}
                        className={`absolute left-1.5 right-1.5 rounded-md border text-left px-2 py-1 transition-all hover:shadow-md hover:z-10 cursor-${canDrag ? "grab" : "pointer"} active:cursor-grabbing ${style.cls}`}
                        style={{ top, height }}
                        title={`${a.time} · ${a.patient_name} · ${typeLabel(a.appointment_type || a.type)}${canDrag ? " · drag to reschedule" : ""}`}
                    >
                        <div className="text-[10px] font-bold leading-tight">{a.time}</div>
                        <div className="text-[11px] font-semibold leading-tight truncate">
                            {a.patient_name || "Patient"}
                        </div>
                        {height > 36 && (
                            <div className="text-[10px] opacity-80 leading-tight truncate">
                                {typeLabel(a.appointment_type || a.type)}
                            </div>
                        )}
                    </div>
                );
            })}
            {/* Drag-over ghost slot */}
            {hoverMinutes != null && (
                <div
                    className="absolute left-1.5 right-1.5 rounded-md border-2 border-dashed border-primary bg-primary/10 pointer-events-none"
                    style={{ top: hoverMinutes * PX_PER_MINUTE, height: 30 * PX_PER_MINUTE - 2 }}
                />
            )}
        </div>
    );
}

/* ───────────────────── Week-grid calendar view ─────────────────────
   Seven columns (Mon–Sun). Drag works exactly as in Day view; dropping
   on a different day reschedules to that day. Hour rail shared.
*/

function WeekGridView({ weekStart, appts, onOpen, onDragReschedule }) {
    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
    const slotLines = [];
    for (let m = 0; m <= TOTAL_MINUTES; m += SLOT_MINUTES) slotLines.push(m);

    const apptsByDay = useMemo(() => {
        const map = new Map();
        for (const d of days) map.set(d, []);
        for (const a of appts) {
            if (map.has(a.date)) map.get(a.date).push(a);
        }
        return map;
    }, [appts, days]);

    const totalCount = days.reduce((n, d) => n + (apptsByDay.get(d)?.length || 0), 0);

    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="week-grid">
            <div className="px-5 py-3.5 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
                        <CalendarBlank size={16} weight="duotone" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Week grid</div>
                        <div className="font-display text-base font-bold leading-tight">
                            {new Date(weekStart + "T00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" })} – {new Date(addDays(weekStart, 6) + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </div>
                    </div>
                </div>
                <div className="text-xs text-muted-foreground">
                    {totalCount === 0 ? "Quiet week" : `${totalCount} ${totalCount === 1 ? "appointment" : "appointments"}`}
                </div>
            </div>
            <div className="flex overflow-x-auto">
                <HourRail />
                <div className="flex-1 grid min-w-[840px]" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                    {days.map((d) => {
                        const items = apptsByDay.get(d) || [];
                        const fakeColumn = { id: d, label: d, items };
                        const dayLabel = new Date(d + "T00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
                        const isToday = d === todayISO();
                        return (
                            <DayColumn
                                key={d}
                                column={fakeColumn}
                                slotLines={slotLines}
                                date={d}
                                onOpen={onOpen}
                                onDragReschedule={onDragReschedule}
                                multi
                                dayLabel={isToday ? `${dayLabel} · TODAY` : dayLabel}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}


/* ───────────────────── Month-grid calendar view ─────────────────────
   Traditional 6-row × 7-col calendar. Each cell shows the date number +
   appointment count + up to 3 compact rows. Click a cell to jump to the
   Day view for that date. (Drag-reschedule disabled at month scale — at
   30+ days visible, drop targets get too fiddly to use accurately.)
*/

function MonthGridView({ monthOf, appts, onPickDay }) {
    const days = useMemo(() => monthRangeDays(monthOf), [monthOf]);
    const monthIdx = new Date(monthOf + "T00:00").getMonth();
    const apptsByDay = useMemo(() => {
        const m = new Map();
        for (const a of appts) {
            if (!m.has(a.date)) m.set(a.date, []);
            m.get(a.date).push(a);
        }
        return m;
    }, [appts]);

    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="month-grid">
            <div className="px-5 py-3.5 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
                        <CalendarBlank size={16} weight="duotone" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Month</div>
                        <div className="font-display text-base font-bold leading-tight">
                            {new Date(monthOf + "T00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                        </div>
                    </div>
                </div>
                <div className="text-xs text-muted-foreground">Click any day to open</div>
            </div>
            <div className="grid grid-cols-7 text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1 pt-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                    <div key={d} className="px-2 py-1.5">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-border p-px">
                {days.map((d) => {
                    const inMonth = new Date(d + "T00:00").getMonth() === monthIdx;
                    const items = apptsByDay.get(d) || [];
                    const isToday = d === todayISO();
                    const active = items.filter((a) => ACTIVE_STATES.has(a.status));
                    return (
                        <button
                            key={d}
                            onClick={() => onPickDay(d)}
                            data-testid={`month-cell-${d}`}
                            className={`bg-card text-left p-2 min-h-[88px] hover:bg-secondary/40 transition-colors ${
                                inMonth ? "" : "opacity-40"
                            } ${isToday ? "ring-2 ring-primary ring-inset" : ""}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className={`text-sm font-semibold ${isToday ? "text-primary" : "text-foreground"}`}>
                                    {new Date(d + "T00:00").getDate()}
                                </span>
                                {items.length > 0 && (
                                    <span className="text-[10px] font-bold rounded-full bg-primary/10 text-primary px-1.5 py-0.5">
                                        {active.length}
                                    </span>
                                )}
                            </div>
                            <div className="mt-1 space-y-0.5">
                                {items.slice(0, 3).map((a) => {
                                    const style = STATUS_STYLE[a.status] || STATUS_STYLE.scheduled;
                                    return (
                                        <div
                                            key={a.id}
                                            className={`text-[10px] rounded px-1 py-0.5 truncate ${style.cls}`}
                                            title={`${a.time} · ${a.patient_name}`}
                                        >
                                            <span className="font-mono">{a.time}</span> {a.patient_name || "Patient"}
                                        </div>
                                    );
                                })}
                                {items.length > 3 && (
                                    <div className="text-[10px] text-muted-foreground font-medium">+{items.length - 3} more</div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}



/* ─────────────────── iter98.28 — Stat strip ─────────────────── */
function AppointmentsStatStrip({ appts, buckets }) {
    const stats = useMemo(() => {
        const t = todayISO();
        const today = appts.filter((a) => a.date === t);
        const todayRemaining = today.filter((a) => ACTIVE_STATES.has(a.status)).length;

        // This week range — today + next 6 days, inclusive.
        const weekEnd = new Date();
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekEndStr = weekEnd.toISOString().slice(0, 10);
        const thisWeek = appts.filter((a) => a.date >= t && a.date <= weekEndStr && !["cancelled", "no_show", "rescheduled"].includes(a.status));

        const confirmed = (buckets.upcoming || []).filter((a) => a.status === "confirmed" || a.status === "arrived" || a.status === "in_progress").length;
        const upcomingActive = (buckets.upcoming || []).filter((a) => !["cancelled", "no_show", "rescheduled"].includes(a.status)).length;
        const pendingConfirm = (buckets.upcoming || []).filter((a) => a.status === "scheduled" || a.status === "pending").length;
        const confirmRate = upcomingActive > 0 ? Math.round((confirmed / upcomingActive) * 100) : null;

        // No-shows in last 30d
        const since = new Date();
        since.setDate(since.getDate() - 30);
        const sinceStr = since.toISOString().slice(0, 10);
        const noShows30d = appts.filter((a) => a.status === "no_show" && a.date >= sinceStr).length;

        return {
            today: today.length,
            todayRemaining,
            thisWeek: thisWeek.length,
            confirmed,
            confirmRate,
            pendingConfirm,
            noShows30d,
        };
    }, [appts, buckets]);

    return (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="appts-stat-strip">
            <StatPill
                testid="stat-today"
                label="Today's Appointments"
                value={stats.today}
                sub={stats.todayRemaining > 0 ? `${stats.todayRemaining} remaining` : stats.today > 0 ? "All seen" : "Nothing scheduled"}
                subTone={stats.todayRemaining > 0 ? "primary" : "muted"}
                Icon={Calendar}
                iconTone="primary"
            />
            <StatPill
                testid="stat-week"
                label="This Week"
                value={stats.thisWeek}
                sub="Next 7 days"
                Icon={CalendarBlank}
                iconTone="indigo"
            />
            <StatPill
                testid="stat-confirmed"
                label="Confirmed"
                value={stats.confirmed}
                sub={stats.confirmRate != null ? `${stats.confirmRate}% confirmation rate` : "—"}
                subTone="primary"
                Icon={CheckCircle}
                iconTone="primary"
            />
            <StatPill
                testid="stat-pending"
                label="Pending Confirm"
                value={stats.pendingConfirm}
                sub={stats.pendingConfirm > 0 ? "Needs action" : "All confirmed"}
                subTone={stats.pendingConfirm > 0 ? "amber" : "muted"}
                Icon={Warning}
                iconTone="amber"
            />
            <StatPill
                testid="stat-no-shows"
                label="No-shows (30d)"
                value={stats.noShows30d}
                sub={stats.noShows30d > 0 ? "Review each" : "None — nice"}
                subTone={stats.noShows30d > 0 ? "rose" : "muted"}
                Icon={XCircle}
                iconTone="rose"
            />
        </div>
    );
}

function StatPill({ testid, label, value, sub, subTone, Icon, iconTone }) {
    const TONE_BG = {
        primary: "bg-primary/10 text-primary",
        amber:   "bg-amber-100 text-amber-700",
        rose:    "bg-rose-100 text-rose-700",
        indigo:  "bg-indigo-100 text-indigo-700",
        neutral: "bg-secondary text-muted-foreground",
    };
    const subColor = {
        primary: "text-primary",
        amber:   "text-amber-700",
        rose:    "text-rose-700",
        muted:   "text-muted-foreground",
    }[subTone] || "text-muted-foreground";
    return (
        <div
            data-testid={testid}
            className="bg-card border border-border rounded-xl p-4 hover:shadow-md hover:border-primary/30 transition-all"
        >
            <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
                <span className={`w-7 h-7 rounded-lg ${TONE_BG[iconTone] || TONE_BG.neutral} grid place-items-center shrink-0`}>
                    <Icon size={14} weight="duotone" />
                </span>
            </div>
            <div className="font-display text-[24px] font-bold tabular-nums leading-none text-foreground">{value}</div>
            <div className={`text-[11px] mt-2 truncate ${subColor}`}>{sub}</div>
        </div>
    );
}


/* ─────────────── Day-grouped appointment list ─────────────── */
function DayGroupedAppointmentList({
    appts, filter, onPreview, onArrive, onStart, onComplete, onWhatsApp,
    onReschedule, onCancel, onNoShow, onRestore, onJumpToDay,
}) {
    const groups = useMemo(() => {
        const map = new Map();
        for (const a of appts) {
            const k = a.date || "—";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(a);
        }
        // Sort each day's appointments by time.
        for (const list of map.values()) {
            list.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
        }
        // Iteration order: chronological for upcoming/past, reverse for past.
        const keys = Array.from(map.keys());
        keys.sort((a, b) => a.localeCompare(b));
        if (filter === "past" || filter === "closed") keys.reverse();
        return keys.map((k) => ({ date: k, items: map.get(k) }));
    }, [appts, filter]);

    return (
        <div className="space-y-6" data-testid="day-grouped-list">
            {groups.map((g) => (
                <DayGroup
                    key={g.date}
                    date={g.date}
                    items={g.items}
                    onPreview={onPreview}
                    onArrive={onArrive}
                    onStart={onStart}
                    onComplete={onComplete}
                    onWhatsApp={onWhatsApp}
                    onReschedule={onReschedule}
                    onCancel={onCancel}
                    onNoShow={onNoShow}
                    onRestore={onRestore}
                    onJumpToDay={onJumpToDay}
                />
            ))}
        </div>
    );
}

function dateLabelFor(iso) {
    const t = todayISO();
    if (iso === t) return { label: "Today", isToday: true };
    const tom = new Date(); tom.setDate(tom.getDate() + 1);
    const tomIso = tom.toISOString().slice(0, 10);
    if (iso === tomIso) return { label: "Tomorrow" };
    const yest = new Date(); yest.setDate(yest.getDate() - 1);
    const yestIso = yest.toISOString().slice(0, 10);
    if (iso === yestIso) return { label: "Yesterday" };
    return { label: new Date(iso).toLocaleDateString([], { weekday: "long" }) };
}

function DayGroup({
    date, items, onPreview, onArrive, onStart, onComplete, onWhatsApp,
    onReschedule, onCancel, onNoShow, onRestore, onJumpToDay,
}) {
    const meta = dateLabelFor(date);
    const fullDate = new Date(date).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short", year: "numeric" });

    return (
        <div className="space-y-2">
            <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${meta.isToday ? "bg-primary text-white" : "bg-primary/10 text-primary"}`}>
                        <Calendar size={16} weight="duotone" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold inline-flex items-center gap-2">
                            {meta.label}
                            {meta.isToday && (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-primary text-white rounded-full px-1.5 py-0.5">Today</span>
                            )}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{fullDate}</div>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px] text-muted-foreground bg-secondary/60 border border-border rounded-full px-2.5 py-0.5">
                        {items.length} appointment{items.length === 1 ? "" : "s"}
                    </span>
                    {meta.isToday && onJumpToDay && (
                        <button
                            onClick={onJumpToDay}
                            className="text-[11px] text-primary font-semibold hover:underline inline-flex items-center gap-1"
                            data-testid="open-day-view-link"
                        >
                            Open Day view →
                        </button>
                    )}
                </div>
            </div>
            <div className="space-y-2">
                {items.map((a) => (
                    <AppointmentRow
                        key={a.id}
                        appt={a}
                        onPreview={onPreview}
                        onArrive={() => onArrive(a)}
                        onStart={() => onStart(a)}
                        onComplete={() => onComplete(a)}
                        onWhatsApp={() => onWhatsApp(a)}
                        onReschedule={() => onReschedule(a)}
                        onCancel={() => onCancel(a)}
                        onNoShow={() => onNoShow(a)}
                        onRestore={() => onRestore(a)}
                    />
                ))}
            </div>
        </div>
    );
}


/* ─────────────── Appointment preview modal (centered popup) ─────────────── */
function AppointmentPreviewModal({ appt, onClose, onArrive, onComplete, onWhatsApp, onReschedule, onCancel }) {
    if (!appt) return null;
    const a = appt;
    const status = a.status || "scheduled";
    const style = STATUS_STYLE[status] || STATUS_STYLE.scheduled;
    const type = a.appointment_type || a.type || "consultation";
    const duration = a.duration_minutes || 30;
    const isActive = ACTIVE_STATES.has(status);
    const fullDate = a.date
        ? new Date(a.date).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short", year: "numeric" })
        : "—";

    return (
        <Dialog open={!!appt} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="max-w-2xl p-0 gap-0 overflow-hidden"
                data-testid="appointment-preview-modal"
            >
                <DialogHeader className="px-5 py-4 border-b border-border bg-gradient-to-br from-primary/5 to-card">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                            <Calendar size={18} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                            <DialogTitle className="text-[15px] truncate text-left inline-flex items-center gap-2">
                                {a.patient_name || "Patient"}
                                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${style.cls}`}>
                                    {style.label}
                                </span>
                            </DialogTitle>
                            <DialogDescription className="text-[11px] text-muted-foreground truncate text-left mt-0.5">
                                {a.time} · {typeLabel(type)} · {fullDate}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto p-5 space-y-4">
                    {/* Contact strip */}
                    <div className="grid grid-cols-3 gap-2">
                        <PreviewContactChip
                            Icon={Phone}
                            label="Call"
                            href={a.patient_phone ? `tel:${a.patient_phone.replace(/\s/g, "")}` : null}
                            disabled={!a.patient_phone}
                            testid="preview-call-btn"
                        />
                        <PreviewContactChip
                            Icon={ChatCircleDots}
                            label="WhatsApp"
                            onClick={onWhatsApp}
                            disabled={!a.patient_phone}
                            testid="preview-wa-btn"
                            accent="emerald"
                        />
                        <PreviewContactChip
                            Icon={EnvelopeSimple}
                            label="Email"
                            href={a.patient_email ? `mailto:${a.patient_email}` : null}
                            disabled={!a.patient_email}
                            testid="preview-email-btn"
                        />
                    </div>
                    {/* Two-column body */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <PreviewSection title="Appointment details" Icon={Stethoscope}>
                            <PreviewRow k="Date & Time" v={`${fullDate}, ${a.time || "—"}`} />
                            <PreviewRow k="Duration" v={`${duration} min`} />
                            <PreviewRow k="Type" v={typeLabel(type)} />
                            <PreviewRow k="Location" v={a.practice_location_name || a.clinic_name || "Primary"} />
                            <PreviewRow k="Source" v={a.walk_in ? "Walk-in" : a.source === "patient_self" ? "Patient (Web)" : a.source ? a.source : "Phone"} />
                            <PreviewRow k="Status" v={style.label} />
                        </PreviewSection>
                        <PreviewSection title="Patient" Icon={IdentificationCard}>
                            <PreviewRow k="Patient ID" v={humanPatientId(a.patient_id)} />
                            <PreviewRow k="Phone" v={a.patient_phone || "—"} />
                            <PreviewRow k="Email" v={a.patient_email || "—"} />
                            <PreviewRow k="Prior visits" v={a.patient_visit_count != null ? String(a.patient_visit_count) : "—"} />
                            {a.patient_no_show_count > 0 && (
                                <PreviewRow k="No-shows" v={
                                    <span className="text-rose-700 font-semibold inline-flex items-center gap-1">
                                        <Warning size={11} weight="fill" /> {a.patient_no_show_count}
                                    </span>
                                } />
                            )}
                        </PreviewSection>
                    </div>
                    {(a.notes || a.staff_notes) && (
                        <div className="bg-secondary/30 border border-border rounded-lg p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                            <div className="text-xs text-foreground/80 italic">
                                {a.staff_notes
                                    ? <>
                                        <div className="not-italic font-medium text-amber-700 mb-0.5">[Front desk]</div>
                                        {a.staff_notes}
                                    </>
                                    : <>
                                        {/* iter98.37 — Patient-self note label so the
                                            doctor distinguishes their own ask from
                                            internal front-desk scribbles. */}
                                        {(a.source === "patient_self" || a.source === "self") && (
                                            <div className="not-italic font-medium text-primary mb-0.5">[From patient]</div>
                                        )}
                                        {a.notes}
                                    </>
                                }
                            </div>
                        </div>
                    )}
                    {a.cancellation_reason && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700">
                            <span className="font-semibold">Cancelled:</span> {a.cancellation_reason}
                        </div>
                    )}
                </div>
                {/* Footer actions */}
                <div className="flex flex-wrap gap-2 px-5 py-3 border-t border-border bg-secondary/30">
                    {isActive && (status === "scheduled" || status === "pending" || status === "confirmed") && (
                        <Button size="sm" onClick={onArrive} className="rounded-md" data-testid="preview-arrive-btn">
                            <UserPlus size={13} className="mr-1.5" /> Arrived
                        </Button>
                    )}
                    {isActive && (status === "arrived" || status === "in_progress") && (
                        <Button size="sm" onClick={onComplete} className="rounded-md" data-testid="preview-complete-btn">
                            <CheckCircle size={13} className="mr-1.5" /> Mark complete
                        </Button>
                    )}
                    {isActive && (
                        <Button size="sm" variant="outline" onClick={onReschedule} className="rounded-md" data-testid="preview-reschedule-btn">
                            <ArrowsClockwise size={13} className="mr-1.5" /> Reschedule
                        </Button>
                    )}
                    {isActive && (
                        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-md text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700" data-testid="preview-cancel-btn">
                            <XCircle size={13} className="mr-1.5" /> Cancel
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onClose} className="rounded-md ml-auto" data-testid="preview-close-btn">
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PreviewSection({ title, Icon, children }) {
    return (
        <div className="bg-secondary/30 border border-border rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1">
                {Icon && <Icon size={11} weight="duotone" />} {title}
            </div>
            <div>{children}</div>
        </div>
    );
}

function PreviewRow({ k, v }) {
    return (
        <div className="flex items-center justify-between text-[12px] py-1">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-medium text-right truncate ml-2">{v}</span>
        </div>
    );
}

function PreviewContactChip({ Icon, label, href, onClick, external, disabled, accent, testid }) {
    const accentCls = accent === "emerald"
        ? "hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
        : "hover:bg-primary/5 hover:border-primary/40 hover:text-primary";
    const base = `flex items-center justify-center gap-1.5 h-9 rounded-md border text-xs font-medium transition-colors ${
        disabled
            ? "bg-secondary/40 border-border text-muted-foreground/50 cursor-not-allowed"
            : `bg-card border-border text-foreground ${accentCls}`
    }`;
    if (disabled) {
        return <div className={base} data-testid={testid} aria-disabled><Icon size={13} weight="duotone" /> {label}</div>;
    }
    if (href) {
        return (
            <a
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                className={base}
                data-testid={testid}
            >
                <Icon size={13} weight="duotone" /> {label}
            </a>
        );
    }
    return (
        <button onClick={onClick} className={base} data-testid={testid}>
            <Icon size={13} weight="duotone" /> {label}
        </button>
    );
}
